import stringHashCode from "lib/utils/stringHashCode";
import { createNanoEvents } from "nanoevents";
import type { IRunScreenerInput } from "types/graphqlTypes";

const emitter = createNanoEvents<{
  change: (input: IRunScreenerInput) => void;
}>();

let lastSuccessfullyExecutedInput: IRunScreenerInput | null = null;
const inputStack: Array<{
  input: IRunScreenerInput;
  timeStamp: Date;
  hashCode: number;
}> = [];
let pendingGridKeyInvalidation: string | null = null;
let originalExecutedInput: IRunScreenerInput | null = null;

function canPushGridKeyInvalidation() {
  if (lastSuccessfullyExecutedInput !== null && originalExecutedInput !== null) {
    const hashA = stringHashCode(JSON.stringify(lastSuccessfullyExecutedInput));
    const hashB = stringHashCode(JSON.stringify(originalExecutedInput));
    return hashA !== hashB;
  }
  return true;
}

const RunScreenerInput = {
  onInputChange: (callback: (input: IRunScreenerInput) => void) => {
    return emitter.on("change", callback);
  },

  canPushGridKeyInvalidation,

  pushGridKeyInvalidation() {
    if (!canPushGridKeyInvalidation()) {
      return;
    }
    pendingGridKeyInvalidation = new Date().toISOString();
  },

  popGridKeyInvalidation() {
    const key = pendingGridKeyInvalidation;
    pendingGridKeyInvalidation = null;
    return key;
  },

  mergeWithLastInput(input: Partial<IRunScreenerInput>) {
    if (lastSuccessfullyExecutedInput) {
      return RunScreenerInput.setLastSuccessfullyExecutedInput({
        ...lastSuccessfullyExecutedInput,
        ...input,
      });
    }
    return null;
  },

  resetOriginalInput() {
    originalExecutedInput = null;
  },

  setLastSuccessfullyExecutedInput(input: IRunScreenerInput) {
    lastSuccessfullyExecutedInput = input;
    if (originalExecutedInput === null) {
      originalExecutedInput = input;
    }

    const hashCode = stringHashCode(JSON.stringify(input));
    const isSameAsBefore = inputStack.length > 1 && inputStack[inputStack.length - 1]?.hashCode === hashCode;
    if (!isSameAsBefore) {
      inputStack.push({
        input,
        timeStamp: new Date(),
        hashCode,
      });
      // Keep only the latest 10 inputs
      if (inputStack.length > 10) {
        inputStack.shift();
      }
      // Only start saving after the initial input
      if (inputStack.length > 1) {
        emitter.emit("change", input);
      }
    }
  },
  getLastSuccessfullyExecutedInput() {
    return lastSuccessfullyExecutedInput;
  },
};

export { RunScreenerInput };
